/** Fields of the password-recovery form at `/auth/update-password`. */

import { Eye, EyeOff } from '@untitled-ui/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthTextField } from '@/components/auth/auth-text-field';
import { FormField } from '@/components/base/form/hook-form';
import { Button } from '@/components/base/buttons/button';

import type { Control } from 'react-hook-form';
import type { AuthPageChrome } from '@/components/auth/auth-page-chrome';
import type { UpdatePasswordFormData } from '@/components/auth/update-password-schema';

/** One revealable password field; owns its own visibility state. */
export function RevealablePasswordField({
  control,
  name,
  label,
  placeholder,
  chrome,
}: Readonly<{
  control: Control<UpdatePasswordFormData>;
  name: 'password' | 'confirmPassword';
  label: string;
  placeholder: string;
  chrome: AuthPageChrome;
}>) {
  const { t } = useTranslation('auth');
  const [show, setShow] = useState(false);

  return (
    <FormField control={control} name={name}>
      {({ field, fieldState }) => (
        <AuthTextField
          {...field}
          autoComplete="new-password"
          hint={fieldState.error?.message}
          inputClassName={chrome.passwordInput}
          isInvalid={fieldState.invalid}
          label={label}
          labelClassName={chrome.label}
          placeholder={placeholder}
          trailing={
            <Button
              aria-label={show ? t('login.hidePassword') : t('login.showPassword')}
              className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
              color="tertiary"
              size="sm"
              type="button"
              onClick={() => setShow(!show)}
            >
              {show ? (
                <EyeOff aria-hidden="true" className={`size-5 ${chrome.revealIcon}`} />
              ) : (
                <Eye aria-hidden="true" className={`size-5 ${chrome.revealIcon}`} />
              )}
            </Button>
          }
          type={show ? 'text' : 'password'}
        />
      )}
    </FormField>
  );
}

/** Authenticator code for an account with TOTP; digits only, six at most. */
export function RecoveryCodeField({
  control,
  chrome,
}: Readonly<{ control: Control<UpdatePasswordFormData>; chrome: AuthPageChrome }>) {
  const { t } = useTranslation('auth');

  return (
    <div className="flex flex-col gap-2">
      <p className={chrome.description}>{t('updatePassword.codeDescription')}</p>
      <FormField control={control} name="totpCode">
        {({ field, fieldState }) => (
          <AuthTextField
            {...field}
            autoComplete="one-time-code"
            hint={fieldState.error?.message}
            inputClassName={chrome.passwordInput}
            inputMode="numeric"
            isInvalid={fieldState.invalid}
            label={t('updatePassword.codeLabel')}
            labelClassName={chrome.label}
            maxLength={6}
            placeholder="000000"
            onChange={(value) => field.onChange(value.replaceAll(/\D/g, '').slice(0, 6))}
          />
        )}
      </FormField>
    </div>
  );
}
